import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LegalPageShell } from '../../components/Layout/LegalPageShell';

export function TermsOfService() {
  const { t } = useTranslation();
  return (
    <LegalPageShell
      title={t('legal.termsOfService.title')}
      lastUpdated={t('legal.termsOfService.lastUpdated')}
    >
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  1. Agreement to Terms
                </h2>
                <p className="text-room-text-dim mb-4">
                  Welcome to CalendIn™! These Terms of Service ("Terms") govern your access to and use of CalendIn's website, services, and applications (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms.
                </p>
                <p className="text-room-text-dim">
                  If you do not agree to these Terms, you may not access or use the Service. We reserve the right to update these Terms at any time, and your continued use of the Service constitutes acceptance of any changes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  2. Description of Service
                </h2>
                <p className="text-room-text-dim mb-4">
                  CalendIn is an instant group calendar coordination tool that enables users to:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2">
                  <li>Create shareable calendar rooms with unique URLs</li>
                  <li>Join existing rooms anonymously with auto-assigned animal names</li>
                  <li>Mark availability on shared time grids</li>
                  <li>View overlapping availability with other participants</li>
                  <li>Coordinate meeting times in real-time</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  3. Eligibility
                </h2>
                <p className="text-room-text-dim">
                  You must be at least 13 years old to use CalendIn. By using the Service, you represent and warrant that you meet this age requirement and have the legal capacity to enter into these Terms. If you are using the Service on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  4. Account and Usage
                </h2>
                
                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  4.1 No Registration Required
                </h3>
                <p className="text-room-text-dim mb-4">
                  CalendIn does not require account registration. Users are identified by browser fingerprints and automatically assigned participant names (e.g., "Fox," "Bear") within each room.
                </p>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  4.2 Acceptable Use
                </h3>
                <p className="text-room-text-dim mb-4">
                  You agree to use CalendIn only for lawful purposes and in accordance with these Terms. You agree NOT to:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2">
                  <li>Use the Service for any illegal or unauthorized purpose</li>
                  <li>Violate any laws in your jurisdiction</li>
                  <li>Attempt to gain unauthorized access to the Service or its systems</li>
                  <li>Interfere with or disrupt the Service or servers</li>
                  <li>Use automated systems (bots, scrapers) to access the Service without permission</li>
                  <li>Create excessive rooms or spam the Service</li>
                  <li>Impersonate others or misrepresent your identity</li>
                  <li>Transmit malicious code, viruses, or harmful content</li>
                  <li>Harass, abuse, or harm other users</li>
                  <li>Use the Service to collect information about other users</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  5. Room Links and Privacy
                </h2>
                <p className="text-room-text-dim mb-4">
                  <strong>Room URLs are the only access control mechanism.</strong> Anyone with a room link can join and see all participant availability data in that room. Key points:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li>Room links are generated randomly but are not password-protected</li>
                  <li>Share room links only with people you want to have access</li>
                  <li>We are not responsible for unauthorized access if you share a room link publicly</li>
                  <li>Anyone joining a room can see all participants' names and availability</li>
                  <li>There is no "delete room" feature—rooms expire automatically after 60 days of inactivity</li>
                </ul>
                <p className="text-room-text-dim">
                  You are responsible for managing who has access to your room links.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  6. Data Retention and Deletion
                </h2>
                <p className="text-room-text-dim">
                  All room data (including participant names and availability selections) is automatically deleted 60 days after the last activity in that room. This deletion is permanent and cannot be recovered. By using the Service, you acknowledge and accept this data retention policy. For more details, see our <Link to="/privacy" className="text-room-accent hover:underline">Privacy Policy</Link>.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  7. Intellectual Property and Copyright
                </h2>
                
                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  7.1 CalendIn Ownership
                </h3>
                <p className="text-room-text-dim mb-4">
                  CalendIn and all related trademarks, service marks, trade names, logos, and brand features are the exclusive property of CalendIn and are protected by international copyright, trademark, and other intellectual property laws. The CalendIn name, logo, and all associated branding elements are proprietary and may not be used without express written permission.
                </p>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  7.2 Service Content and Features
                </h3>
                <p className="text-room-text-dim mb-4">
                  All original content, features, functionality, source code, algorithms, user interface designs, and other materials comprising the CalendIn service are proprietary and protected intellectual property. This includes but is not limited to:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li>The calendar coordination algorithm and availability matching system</li>
                  <li>User interface designs, layouts, and visual elements</li>
                  <li>Real-time collaboration features and WebSocket implementations</li>
                  <li>Browser fingerprinting and participant management systems</li>
                  <li>Database schemas and data processing methods</li>
                  <li>All software, documentation, and technical specifications</li>
                </ul>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  7.3 Prohibited Uses
                </h3>
                <p className="text-room-text-dim mb-4">
                  You may NOT:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li>Copy, modify, distribute, or create derivative works of the Service</li>
                  <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                  <li>Use CalendIn trademarks, logos, or branding without permission</li>
                  <li>Create competing services that replicate CalendIn's functionality</li>
                  <li>Extract or scrape data from the Service for commercial purposes</li>
                  <li>Use automated tools to access or interact with the Service</li>
                </ul>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  7.4 User Data Ownership
                </h3>
                <p className="text-room-text-dim">
                  You retain ownership of any availability data you input into the Service. By using the Service, you grant us a limited, non-exclusive license to use, store, and display your data solely to provide the Service functionality. This license terminates when your data is deleted according to our retention policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  8. Disclaimers and Limitation of Liability
                </h2>
                
                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  8.1 "AS-IS" Service
                </h3>
                <p className="text-room-text-dim mb-4">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li>Warranties of merchantability, fitness for a particular purpose, or non-infringement</li>
                  <li>Guarantees of availability, reliability, accuracy, or completeness</li>
                  <li>Guarantees that the Service will be error-free or uninterrupted</li>
                  <li>Guarantees that defects will be corrected</li>
                </ul>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  8.2 Limitation of Liability
                </h3>
                <p className="text-room-text-dim mb-4">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, CALENDIN AND ITS OPERATORS SHALL NOT BE LIABLE FOR:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                  <li>Loss of profits, data, use, or goodwill</li>
                  <li>Service interruptions or unavailability</li>
                  <li>Errors, mistakes, or inaccuracies in the Service</li>
                  <li>Unauthorized access to your data</li>
                  <li>Any other matter relating to the Service</li>
                </ul>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  8.3 No Responsibility for Third-Party Content
                </h3>
                <p className="text-room-text-dim">
                  We are not responsible for the behavior of other users or the accuracy of availability information entered by participants.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  9. Indemnification
                </h2>
                <p className="text-room-text-dim">
                  You agree to indemnify, defend, and hold harmless CalendIn and its operators from any claims, losses, damages, liabilities, and expenses (including legal fees) arising from: (a) your use of the Service, (b) your violation of these Terms, or (c) your violation of any rights of another person or entity.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  10. Termination
                </h2>
                <p className="text-room-text-dim mb-4">
                  We reserve the right to:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li>Terminate or suspend your access to the Service at any time, without notice, for any reason</li>
                  <li>Delete rooms or data that violate these Terms</li>
                  <li>Refuse service to anyone for any reason</li>
                </ul>
                <p className="text-room-text-dim">
                  You may stop using the Service at any time. Upon termination, your data will be deleted according to our standard 60-day retention policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  11. Changes to the Service
                </h2>
                <p className="text-room-text-dim">
                  We reserve the right to modify, suspend, or discontinue the CalendIn Service (or any part thereof) at any time, with or without notice. We will not be liable to you or any third party for any modification, suspension, or discontinuation of the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  12. Changes to These Terms
                </h2>
                <p className="text-room-text-dim mb-4">
                  We may update these Terms from time to time. When we do:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li>We will update the "Last updated" date at the top</li>
                  <li>Significant changes will be communicated through the Service</li>
                  <li>Your continued use after changes constitutes acceptance</li>
                </ul>
                <p className="text-room-text-dim">
                  If you do not agree to the updated Terms, you must stop using the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  13. Governing Law and Disputes
                </h2>
                <p className="text-room-text-dim mb-4">
                  These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which CalendIn operates, without regard to its conflict of law provisions.
                </p>
                <p className="text-room-text-dim">
                  Any disputes arising from these Terms or the Service shall be resolved through good faith negotiations. If negotiations fail, disputes shall be resolved through binding arbitration or in the courts of the jurisdiction in which CalendIn operates.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  14. Miscellaneous
                </h2>
                <ul className="list-disc list-inside text-room-text-dim space-y-2">
                  <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and CalendIn regarding the Service</li>
                  <li><strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions will remain in effect</li>
                  <li><strong>Waiver:</strong> Our failure to enforce any right or provision does not constitute a waiver</li>
                  <li><strong>Assignment:</strong> You may not assign these Terms; we may assign them without restriction</li>
                  <li><strong>No Third-Party Benefits:</strong> These Terms do not create rights for third parties</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  15. Contact Us
                </h2>
                <p className="text-room-text-dim mb-4">
                  If you have questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <p className="text-room-text-dim">
                    <strong>Email:</strong> <a href="mailto:info@calendin.app" className="text-room-accent hover:underline">info@calendin.app</a>
                  </p>
                  <p className="text-room-text-dim mt-2">
                    <strong>Service:</strong> CalendIn
                  </p>
                </div>
              </section>
    </LegalPageShell>
  );
}

